import NextImage from 'next/image';

import React, { useMemo } from 'react';

import styled from 'styled-components';

import VerifiedPartner from '@components/partners/VerifiedPartner';

import { getPerformingCount } from '@utils/get-performing-count';

import { FeedbackSummaryDto, Partner } from '@customTypes/Partner';
import { PartnerPageTemplate } from '@customTypes/PartnerPageTemplate';

interface Props {
  partner: Partner;
  template?: PartnerPageTemplate;

  feedbackSummary: FeedbackSummaryDto;
  focusOn: (target: string) => void;
}

const Wrapper = styled.div`
  .profile {
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-bottom: 30px;

    .left {
      position: relative;
      img {
        border-radius: 50px;
        width: 72px;
        height: 72px;
        @media (min-width: 992px) {
          width: 96px;
          height: 96px;
        }
      }
    }
    .right {
      transform: translateY(-4px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      .badge {
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 700;
        color: #055cf5;
        @media (min-width: 992px) {
          font-size: 16px;
        }
        img {
          height: 16px;
          margin-right: 2px;
          @media (min-width: 992px) {
            height: 20px;
            margin-right: 1.5px;
          }
        }
      }
      .name {
        font-size: 16px;
        line-height: 150%;
        @media (min-width: 992px) {
          font-size: 28px;
        }
        font-weight: 700;
      }
      .description {
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: 2px;
        img {
          width: 14px;
          height: 18px;
          @media (min-width: 992px) {
            width: 18px;
          }
        }
        .star-rating {
          font-size: 14px;
          color: #080808;
          @media (min-width: 992px) {
            font-size: 16px;
          }
        }
        .ratings {
          margin-left: 2px;
          margin-right: 4px;
          font-size: 14px;
          font-weight: 400;
          color: #62656a;
          @media (min-width: 992px) {
            font-size: 16px;
          }
        }
        .performing-count {
          font-size: 14px;
          color: #2e3035;
          @media (min-width: 992px) {
            font-size: 16px;
          }
        }
      }
    }
  }
`;

const Rating = styled.div`
  display: flex;
  cursor: pointer;
`;

const PartnerName: React.FC<Props> = (props) => {
  const { partner, focusOn, feedbackSummary } = props;

  const reviewCounts = useMemo(() => {
    return Object.values(feedbackSummary.scores).reduce((acc, cur) => acc + cur, 0);
  }, [feedbackSummary]);

  return (
    <Wrapper>
      <div className="profile">
        <div className={`left ${partner.verified ? 'verified' : ''}`}>
          <NextImage
            src={partner.profile ?? '/svg/partner_profile_default.svg'}
            alt={partner.name ?? 'Partner'}
            width={72}
            height={72}
          />
        </div>
        <div className="right">
          {partner.verified ? (
            <div className="badge">
              <img src={'/svg/verified.svg'} />
              <span>미소 인증</span>
            </div>
          ) : null}
          <div className="name">{partner.name}</div>
          <div className="description">
            <NextImage src={'/svg/starRating.svg'} alt={'Star'} width={18} height={18} />
            <Rating onClick={() => focusOn('review')}>
              <div className="star-rating">{feedbackSummary.avg.toFixed(2)}</div>
              <div className="ratings">({reviewCounts})</div>
            </Rating>
            <span className="performing-count">{getPerformingCount(partner.request_count)}</span>
          </div>
        </div>
      </div>
      {partner.verified ? <VerifiedPartner /> : null}
    </Wrapper>
  );
};

export default PartnerName;
